import { useRef, useState } from "react";
import { api, type EmployeeRow } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useFinanceText, financeToday } from "../lib/finance-text";
import { useFinance } from "../lib/use-finance";

export type FinanceField = { name: string; label?: string; type?: string; value?: string; optional?: boolean; options?: { value: string; label: string }[] };

export function FinanceForm({ fields, path, method, extra, onSaved, submit, embedded = false, submitLabel }: {
  fields: FinanceField[]; path?: string; method?: string; extra?: Record<string, unknown>;
  onSaved: () => void; submit?: (data: Record<string, unknown>) => Promise<unknown>; embedded?: boolean; submitLabel?: string;
}) {
  const t = useFinanceText();
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(fields.map(f => [f.name, f.value ?? ""])));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const pending = useRef<Record<string, unknown> | null>(null);
  async function save() {
    if (busy) return;
    const data: Record<string, unknown> = { ...extra, operation_key: crypto.randomUUID() };
    for (const field of fields) {
      const value = values[field.name] ?? "";
      if (!field.optional && !value.trim()) { setError(`${t(field.label ?? field.name)}: ${t("empty")}`); return; }
      data[field.name] = field.type === "id" ? (value ? Number(value) : null) : (value || (field.optional && field.type === "date" ? null : ""));
    }
    pending.current ??= data;
    setBusy(true); setError("");
    try {
      if (submit) await submit(pending.current); else await api.financeCommand(path!, pending.current, method);
      pending.current = null; setUncertain(false); onSaved();
    } catch (e) {
      const failure = e as { message?: string; status?: number };
      const unknown = !failure.status || failure.status >= 500;
      setUncertain(unknown);
      if (!unknown) pending.current = null;
      setError(t(failure.message ?? String(e)));
    } finally { setBusy(false); }
  }
  const contents = <>
    <fieldset disabled={busy || uncertain} className="grid gap-3 sm:grid-cols-2">
      {fields.map(field => <label className="grid gap-1 text-sm" key={field.name}>
        <span>{t(field.label ?? field.name)}{field.optional ? ` (${t("optional")})` : ""}</span>
        {field.options ? <select className="h-9 rounded-md border bg-background px-2" value={values[field.name]} onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))}>
          {field.optional && <option value="">—</option>}
          {field.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select> : <Input type={field.type === "id" ? "number" : field.type ?? "text"} step="0.01" value={values[field.name]} required={!field.optional}
          onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))} />}
      </label>)}
    </fieldset>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {uncertain && <p className="text-sm">{t("uncertain")}</p>}
    <Button type={embedded ? "button" : "submit"} disabled={busy} onClick={embedded ? () => void save() : undefined}>{t(uncertain ? "retry" : submitLabel ?? "save")}</Button>
  </>;
  return embedded ? <div className="space-y-3">{contents}</div> : <form className="space-y-3" onSubmit={e => { e.preventDefault(); void save(); }}>{contents}</form>;
}

export function AgreementEditor({ employeeId, onSaved, embedded = false }: { employeeId: number; onSaved: () => void; embedded?: boolean }) {
  const t = useFinanceText();
  return <div className="space-y-3"><p className="text-sm text-muted-foreground">{t("agreementHint")}</p>
    <FinanceForm embedded={embedded} path="payroll/agreements" extra={{ employee_id: employeeId }} onSaved={onSaved} fields={[
      { name: "model", value: "fixed", options: ["fixed", "percentage", "combined"].map(value => ({ value, label: t(value) })) },
      { name: "effective_date", type: "date", value: financeToday() },
      { name: "fixed", type: "number", value: "0" }, { name: "percent", type: "number", value: "0" }, { name: "reason" },
    ]} />
  </div>;
}

export function EmployeeFinance({ employee }: { employee: EmployeeRow }) {
  const { active } = useFinance();
  const t = useFinanceText();
  const [saved, setSaved] = useState(false);
  if (!active("ish_haqi")) return null;
  return <div className="space-y-4 border-t pt-4">
    <FinanceForm embedded fields={[
      { name: "employment_start", type: "date", optional: true, value: employee.employment_start ?? "" },
      { name: "employment_end", type: "date", optional: true, value: employee.employment_end ?? "" },
    ]} submit={data => api.updateEmployee(employee.id, { employment_start: data.employment_start, employment_end: data.employment_end })} onSaved={() => setSaved(true)} />
    <details><summary className="cursor-pointer text-sm font-medium">{t("agreement")}</summary><AgreementEditor employeeId={employee.id} embedded onSaved={() => setSaved(true)} /></details>
    {saved && <p role="status" className="text-sm">{t("saved")}</p>}
  </div>;
}
